
/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import {
  Component, Input, Output, EventEmitter, ChangeDetectorRef,
  OnChanges, OnInit, OnDestroy, LOCALE_ID, Inject, TemplateRef,
  ChangeDetectionStrategy, ElementRef, ViewChild, AfterViewInit,
  HostListener,
  NgZone
} from "@angular/core";
import { debounceTime, distinctUntilChanged, Subject, Subscription, takeUntil, timer } from "rxjs";
import {
  WeekDay, CalendarEvent, WeekViewAllDayEvent as WeekViewAllDayAppointment,
  WeekView, ViewPeriod, WeekViewHourColumn,
  DayViewEvent, DayViewHourSegment, DayViewHour, WeekViewAllDayEventRow as WeekViewAllDayAppointmentRow
} from "calendar-utils";
import { ResizeEvent } from "angular-resizable-element";
import { CalendarDragHelper } from "../common/calendar-drag-helper.provider";
import { CalendarResizeHelper } from "../common/calendar-resize-helper.provider";
import { CalendarAppointmentTimesChangedEvent, CalendarAppointmentTimesChangedEventType } from "../common/calendar-appointment-times-changed-event.interface";
import { CalendarUtils } from "../common/calendar-utils.provider";
import {
  validateEvents, roundToNearest, trackByWeekDayHeaderDate,
  trackByHourSegment, trackByHour, getMinutesMoved,
  getDefaultEventEnd, getMinimumEventHeightInMinutes, addDaysWithExclusions,
  trackByDayOrWeekEvent, isDraggedWithinPeriod, shouldFireDroppedEvent,
  getWeekViewPeriod
} from "../common/util";
import { DateAdapter } from "../date-adapters/date-adapter";
import { DragEndEvent, DropEvent, DragMoveEvent, ValidateDrag } from "angular-draggable-droppable";
import { PlacementArray } from "positioning";
import { CalendarRepository } from "../../repositories/calendar.repository";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { CalenderUtils } from "../../utils/calender-utils";
import { MailBroadcaster } from "src/app/common/providers/mail-broadcaster.service";
import * as moment from "moment";


import { events } from "./events.data";
import { format } from "date-fns";
import { addMinutes, isSameDay } from "date-fns";
import { MailConstants } from "src/app/common/utils/mail-constants";
import { MailTag } from "src/app/mail/models/mail-tag.model";
import { CommonRepository } from "src/app/mail/repositories/common-repository";
import * as Hammer from "hammerjs";

export interface WeekViewAllDayEventResize {
  originalOffset: number;
  originalSpan: number;
  edge: string;
}

export interface CalendarWeekViewBeforeRenderEvent extends WeekView {
  header: WeekDay[];
}

@Component({
  selector: "vp-calendar-week-view",
  templateUrl: "./calendar-week-view.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarWeekViewComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  constructor(
    protected cdr: ChangeDetectorRef,
    protected utils: CalendarUtils,
    @Inject(LOCALE_ID) locale: string,
    protected dateAdapter: DateAdapter,
    private broadcaster: MailBroadcaster,
    private breakpointObserver: BreakpointObserver,
    public calendarRepository: CalendarRepository,
    private commonRepository: CommonRepository,
    private zone: NgZone
  ) {

     this.calendarRepository.newAppointmentDayViewTimeID$.subscribe(data => {
          this.oneDayViewFrameData(data);
      });
    this.locale = locale;
    this.isMobileScreen = this.breakpointObserver.isMatched("(max-width: 599px)");
    this.hourSegmentHeight = this.isMobileScreen ? 42 : 20;
    this.snapDraggedEvents = false;

    this.calendarRepository.canceNewAppointmentCreate3dayCustom$.subscribe(res => {
      if (calendarRepository.daysInWeek === 3 && this.isMobileScreen) {
         this.allEventFromView = this.allEventFromView.filter(alEv => alEv.id !== 1000111);
         this.isNewInMobile = null;
        this.initCustomeAppointments();
      } if (calendarRepository.daysInWeek === 1) {
          this.newApptOnedayViewFramePositionTop = 0;
      }
    });
    // this.initCustomeAppointments();


  }

  // get maxEventCount(): number {
  //   return Math.max(
  //     ...Object.values(this.allDayForCustomEvent3day).map(arr => arr.length)
  //   );
  // }

  get daysCustom(): Date[] {
    // const base = new Date(2025, 6, 25); // July 25
    const base = new Date(this.selectedYear, this.selectedMonth, this.selectedDate); // July 25
    const maxDays = 30;
    const daysToGenerate = Math.min(this.daysToShow, maxDays); // Prevent accidental huge loops
    return Array.from({ length: daysToGenerate }, (_, i) =>
      addMinutes(base, i * 1440)
    );
  }

  get timeSlots(): string[] {
    const slots: string[] = [];
    for (let hour = this.startHour; hour < this.startHour + this.hoursPerDay; hour++) {
      for (let i = 0; i < this.slotsPerHour; i++) {
        const minutes = i * 15;
        const label = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        slots.push(label);
      }
    }
    return slots;
  }

  get timeSlotForShowingTime(): string[] {
    const slots: string[] = [];
    for (let hour = this.startHour; hour < this.startHour + this.hoursPerDay; hour++) {
      for (let i = 0; i < this.slotsPerHour; i++) {
        const minutes = i * 15;
        const label = `${hour.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
        if (this.locale === "de") {
          slots.push(label);
        } else {
           slots.push(this.convertTo12Hour(label));
        }
      }
    }
    return slots;
  }

  get getTimeMarkerPostition() {
    let currentDate = new Date();
    let hrs = currentDate.getHours();
    let minutes = currentDate.getMinutes();
    let top = hrs * 160;
    top = top + Math.ceil((minutes / 15 ) * 40);

    if (this.view.allDayEventRows.length > 0) {
      top = top + this.view.allDayEventRows.length * 40;
    }
    return top + 40;
  }


  get weekViewCalTimeEventsHeight() {
    let headerHeight = 0;
    let header: any = document.getElementsByClassName("cal-week-view-header");
    if (header && header.length) {
      for (let i = 0 ; i < header.length ; i++) {
        if ( header[i].clientHeight > 0) {
            headerHeight =   header[i].clientHeight + 45 + 38 + 48;
        }
      }
      console.log("weekViewCalTimeEventsHeight", headerHeight);
      const trashSection = document.getElementsByClassName("deleted-appointment-container");
      if (trashSection && trashSection.length) {
        headerHeight = headerHeight + 310;
      }
     return `calc(100vh - ${headerHeight}px)`;
    } else {
       return "100%";
    }
  }
  hasScrolledToCurrentTime: boolean = false;

  newApptOnedayViewFramePositionTop: number = 0;

  new3dayViewCustom: boolean = false;
  @Input() viewDate: Date;
  @Input() events: CalendarEvent[] = [];
  @Input() excludeDays: number[] = [];
  @Input() refresh: Subject<any>;
  @Input() locale: string;
  @Input() tooltipPlacement: PlacementArray = "auto";
  @Input() tooltipTemplate: TemplateRef<any>;
  @Input() tooltipAppendToBody: boolean = true;
  @Input() tooltipDelay: number | null = null;
  @Input() weekStartsOn: number;
  @Input() headerTemplate: TemplateRef<any>;
  @Input() eventTemplate: TemplateRef<any>;
  @Input() eventTitleTemplate: TemplateRef<any>;
  @Input() eventActionsTemplate: TemplateRef<any>;
  @Input() precision: "days" | "minutes" = "days";
  @Input() weekendDays: number[];
  @Input() snapDraggedEvents: boolean = true;
  @Input() hourSegments: number = 4;
  @Input() hourSegmentHeight: number = 30;
  @Input() dayStartHour: number = 0;
  @Input() dayStartMinute: number = 0;
  @Input() dayEndHour: number = 23;
  @Input() dayEndMinute: number = 59;
  @Input() hourSegmentTemplate: TemplateRef<any>;
  @Input() eventSnapSize: number;
  @Input() daysInWeek: number;
  @Output() dayHeaderClicked = new EventEmitter<{ day: WeekDay; }>();
  @Output() eventClicked = new EventEmitter<{ event: CalendarEvent; isSelecting: boolean; }>();
  @Output() appointmentTimesChanging = new EventEmitter<CalendarAppointmentTimesChangedEvent>();
  @Output() appointmentTimesChanged = new EventEmitter<CalendarAppointmentTimesChangedEvent>();
  @Output() beforeViewRender = new EventEmitter<CalendarWeekViewBeforeRenderEvent>();
  @Output() hourSegmentClicked = new EventEmitter<{ date: Date; }>();
  @Output() onViewSwiped = new EventEmitter<any>();
  @Output() onContextMenuClicked = new EventEmitter<any>();
  @ViewChild("weekCalTimeline", {static: false}) weekCalTimeline: ElementRef;
  @ViewChild("weekCalTimeline1Day", {static: false}) weekCalTimeline1Day: ElementRef;

  @Output() handleDoubleClicked = new EventEmitter<{ event: CalendarEvent; }>();
  @Output() hourSegmentContextMenuClicked = new EventEmitter<any>();
  @Output() longPressClicked = new EventEmitter<any>();

  currentTheme = "";
  isMobileScreen: boolean = false;
  private isAlive$ = new Subject<boolean>();
  days: WeekDay[];
  view: WeekView;
  eventRefresh: Subject<any> = new Subject();
  refreshSubscription: Subscription;
  allDayEventResizes: Map<WeekViewAllDayAppointment, WeekViewAllDayEventResize> = new Map();
  timeEventResizes: Map<CalendarEvent, ResizeEvent> = new Map();
  eventDragEnter = 0;
  dragActive = false;
  dragAlreadyMoved = false;
  setupHammer: any;
  validateDrag: ValidateDrag;
  validateResize: (args: any) => boolean;
  dayColumnWidth: number;
  calendarId = Symbol("vp calendar week view id");

  trackByWeekDayHeaderDate = trackByWeekDayHeaderDate;
  trackByHourSegment = trackByHourSegment;
  trackByHour = trackByHour;
  trackByDayOrWeekEvent = trackByDayOrWeekEvent;
  timelineInterval: Subscription;

  currentDayView: number = 0;
  /**
   * @hidden
   */

  currentDayInView: number = 0;

sameTimeEvents: any = [];

extraCellAddInBottom: Array<any> = [];


showAppointment: boolean = false;

counting = 0;



moreHrs: Array<any> = [];

// New custom 3 day view
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////// --- NEW CUSTOM 3 DAY VIEW MOBILE -----///////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  customCellWidth = 80;
  appointments: any = [];

  allEventFromView: any = [];

  // new custom view 3 days

  daysToShow = 3;
  slotsPerHour = 4;
  hoursPerDay = 24; // e.g. 8 AM to 10 PM
  startHour = 0;
  isNewInMobile: any;

  selectedYear: number = 2025;
  selectedMonth: number = 6;
  selectedDate: number = 25;

  allDayForCustomEvent3day = {
    day1: [],
    day2: [],
    day3: [],
  };
  allZimbraTags: MailTag[] = [];

  today = new Date();

    @ViewChild("mydiv") mydiv!: ElementRef;

  // custom drag drop try



  private pressTimer: any;
  private isLongPress: boolean = false;
  private longPressThreshold: number = 250; // Time in ms (1000ms = 1 second)
  private isMousePressed: boolean = false; // Track if the mouse is pressed
  private startPosition: { x: number; y: number } | null = null; // Initial position of the mouse
  private currentPosition: { x: number; y: number } | null = null; // Current position of the mouse

  selectedAppointment: any;
  oldAppointmentLeft: any;
  oldAppointmentTop: any;

    private isPointerActive: boolean = false;
    private startPositionPointer: { x: number; y: number } | null = null; // Initial position of the mouse
    private currentPositionPointer: { x: number; y: number } | null = null;

    oldAptTop: any;
    oldAptHeight: any;
    oldStartY: any;

  isDraggingCustomAppointment: boolean = false;
  timerPointerMoving: any;


  bottomResizeing: boolean = false;
  topResizing: boolean = false;

  // Swipe for date change new custom 3 day view

  private startX: number = 0;
  private startY: number = 0;
  cellWidth3dayVeiwMobile = 0;

  startDate: any;
  day3VisiblityAftersetPosition: boolean = false;
  debounceTimerFor3dayView: any; // keep outside the function, at component/class level


    @Output() handleListClicked = new EventEmitter<any>();
    debounceTimer: any;
    sameAppointmentDiffCalendar: any = [];


  debounceTimerAppointmentClick: any;
  debounceTapHandlerSubject: Subject<{appt: any, dayIndex: number}> = new Subject<{appt: any, dayIndex: number}>();

  groupedAppointmentsData = [];



  private hammerMap = new Map<Element, HammerManager>();

  callScrollTotimerOneday: boolean = false;


  timeoutId: any;

  convertTo12Hour(time24: string): string {
    const [hoursStr, minutes] = time24.split(":");
    const hours = parseInt(hoursStr, 10);
    const suffix = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    return `${hour12}:${minutes} ${suffix}`;
  }

  oneDayViewFrameData(elId) {
      this.newApptOnedayViewFramePositionTop = 0;
      const elementId = elId;
      const element = document.getElementById(elementId);
      if (element) {
        this.newApptOnedayViewFramePositionTop =  element.offsetTop + 168;
      }

  }

  trackByHourColumn = (index: number, column: WeekViewHourColumn) =>
    column.hours[0] ? column.hours[0].segments[0].date.toISOString() : column

  trackById = (index: number, row: WeekViewAllDayAppointmentRow) => row.id;

  @HostListener("window:resize", ["$event"])
  onWindowResize() {
      this.isMobileScreen = this.breakpointObserver.isMatched("(max-width: 599px)");
      this.snapDraggedEvents = false;
      if (this.isMobileScreen) {
        this.getSetWidth();
      }
      const isDayView = this.currentDayView === 1 || this.calendarRepository.daysInWeek === 1;
      if (isDayView) {
        this.hourSegmentHeight = 42;
      } else {
        this.hourSegmentHeight = 20;
      }
      this.cdr.markForCheck();
  }

  relocateTimePointer() {
    for (let i = 0; i < 4; i++) {
      setTimeout(() => {
        CalenderUtils.calendarTimeLineScroll();
      }, i * 500);
    }
  }

  ngOnInit(): void {
    this.debounceTapHandlerSubject.pipe(takeUntil(this.isAlive$), distinctUntilChanged(), debounceTime(250)).subscribe(this.handleAppSelect.bind(this));
      if (this.isMobileScreen) {
        this.getSetWidth();
      }
      this.currentTheme = localStorage.getItem("theme");

      if (this.daysInWeek === 1) {
        this.hasScrolledToCurrentTime = false;
      }

      setTimeout(() => {
        this.relocateTimePointer();
      }, 1000);
    if (this.refresh) {
      this.refreshSubscription = this.refresh.subscribe(() => {
        this.refreshAll();
        this.cdr.markForCheck();
      });
    }

  }
  togetView() {
    console.log("currentDayInView", this.currentDayInView);
  }
  ngOnChanges(changes: any): void {
    if (window.matchMedia("(max-width: 599px)").matches) {
      this.attachSwipeWhenReady("#weekViewCalTimeEvents");
      this.attachSwipeWhenReady("#weekViewCalTimeEvents3Day");
      this.attachSwipeWhenReady("#weekViewCalTimeEventsD1");
    }

    this.calendarRepository.daysInWeek = 0;
    if (changes.daysInWeek && changes.daysInWeek.currentValue) {
      this.currentDayView = changes.daysInWeek.currentValue;
      this.calendarRepository.daysInWeek = changes.daysInWeek.currentValue;
      if (this.currentDayView === 1) {
        this.hourSegmentHeight = 42;
        this.hasScrolledToCurrentTime = false;
        setTimeout(() => {
          CalenderUtils.calendarTimeLineScrollForDayOne();
          this.hasScrolledToCurrentTime = true;
        }, 100);
      }
      setTimeout(() => {
        this.scrollToTimeMarker();
      }, 100);
      this.cdr.markForCheck();
    } else {
      this.currentDayView = this.daysInWeek;
      this.calendarRepository.daysInWeek = this.daysInWeek;
      this.hourSegmentHeight = 20;
      this.cdr.markForCheck();
    }

    console.log("currentDayView days in", this.calendarRepository, this.currentDayView, this.hourSegmentHeight, this.calendarRepository.daysInWeek, this.daysInWeek);

    // custom 3 day view start
    if (this.calendarRepository.daysInWeek === 3 && this.isMobileScreen) {
        this.selectedYear = this.calendarRepository.miniCalSelectedDate.getFullYear();
        this.selectedMonth = this.calendarRepository.miniCalSelectedDate.getMonth();
        this.selectedDate = this.calendarRepository.miniCalSelectedDate.getDate();
      // this.day3VisiblityAftersetPosition = false;

      clearTimeout(this.debounceTimerFor3dayView);
      this.debounceTimerFor3dayView = setTimeout(() => {
       this.initCustomeAppointments();
        setTimeout(() => {
          this.calculateAllDayEventFor3dayViewCustom();
        }, 250);
      }, 1250);
      }



      this.commonRepository.getZimbraTagsList().pipe(takeUntil(this.isAlive$)).subscribe(allTags => {
        this.allZimbraTags = allTags;
      });
    // custom 3 day view end
    if (this.daysInWeek === 1 && this.hasScrolledToCurrentTime ) {
      setTimeout(() => {
          CalenderUtils.calendarTimeLineScrollForDayOne();
          this.hasScrolledToCurrentTime = false;
      }, 500);
    }
    this.cdr.markForCheck();
    const refreshHeader =
      changes.viewDate ||
      changes.excludeDays ||
      changes.weekendDays ||
      changes.daysInWeek ||
      changes.weekStartsOn;

    const refreshBody =
      changes.viewDate ||
      changes.dayStartHour ||
      changes.dayStartMinute ||
      changes.dayEndHour ||
      changes.dayEndMinute ||
      changes.hourSegments ||
      changes.weekStartsOn ||
      changes.weekendDays ||
      changes.excludeDays ||
      changes.hourSegmentHeight ||
      changes.events ||
      changes.daysInWeek;

    if (refreshHeader) {
      this.refreshHeader();
    }

    if (changes.events) {
      validateEvents(this.events);
    }

    if (refreshBody) {
      this.refreshBody();
    }

    if (refreshHeader || refreshBody) {
      this.emitBeforeViewRender();
    }

    setTimeout(() => {
       const timeLine = document.querySelector(".timeline-bar");
        if (timeLine !== null && !!timeLine ) {
          CalenderUtils.calendarTimeLineScroll();
        }
    }, 120);

    this.callScrollTotimerOneday = false;
  }

  /**
   * @hidden
   */
  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.isAlive$.next(false);
      this.isAlive$.unsubscribe();
    }
  }

  protected resizeStarted(eventsContainer: HTMLElement, minWidth?: number) {
    this.dayColumnWidth = this.getDayColumnWidth(eventsContainer);
    const resizeHelper: CalendarResizeHelper = new CalendarResizeHelper(
      eventsContainer,
      minWidth
    );
    this.validateResize = ({ rectangle }) =>
      resizeHelper.validateResize({ rectangle });
    this.cdr.markForCheck();
  }

  /**
   * @hidden
   */
  timeEventResizeStarted(
    eventsContainer: HTMLElement,
    timeEvent: DayViewEvent,
    resizeEvent: ResizeEvent
  ): void {
    this.timeEventResizes.set(timeEvent.event, resizeEvent);
    this.resizeStarted(eventsContainer);
  }

  /**
   * @hidden
   */
  timeEventResizing(timeEvent: DayViewEvent, resizeEvent: ResizeEvent) {
    this.timeEventResizes.set(timeEvent.event, resizeEvent);
    const adjustedEvents = new Map<CalendarEvent, CalendarEvent>();
    const tempEvents = [...this.events];

    this.timeEventResizes.forEach((lastResizeEvent, event) => {
      const newEventDates = this.getTimeEventResizedDates(
        event,
        lastResizeEvent
      );
      const adjustedEvent = { ...event, ...newEventDates };
      adjustedEvents.set(adjustedEvent, event);
      const eventIndex = tempEvents.indexOf(event);
      tempEvents[eventIndex] = adjustedEvent;

      this.appointmentTimesChanging.emit({
        newStart: newEventDates.start,
        newEnd: newEventDates.end,
        event: timeEvent.event,
        type: CalendarAppointmentTimesChangedEventType.Resize
      });
      this.broadcaster.broadcast("appointmentTimeChanged", {
        newStart: newEventDates.start,
        newEnd: newEventDates.end,
        event: timeEvent.event,
        type: CalendarAppointmentTimesChangedEventType.Resize
      });
    });

    this.restoreOriginalEvents(tempEvents, adjustedEvents);
  }

  /**
   * @hidden
   */
  timeEventResizeEnded(timeEvent: DayViewEvent) {
    this.view = this.getWeekView(this.events);
    const lastResizeEvent = this.timeEventResizes.get(timeEvent.event);
    if (lastResizeEvent) {
      // this.timeEventResizes.delete(timeEvent.event);
      const newEventDates = this.getTimeEventResizedDates(
        timeEvent.event,
        lastResizeEvent
      );

      this.appointmentTimesChanged.emit({
        newStart: newEventDates.start,
        newEnd: newEventDates.end,
        event: timeEvent.event,
        type: CalendarAppointmentTimesChangedEventType.Resize
      });
      this.broadcaster.broadcast("appointmentTimeEnded", {
        newStart: newEventDates.start,
        newEnd: newEventDates.end,
        event: timeEvent.event,
        type: CalendarAppointmentTimesChangedEventType.Resize
      });
    }

    setTimeout(() => {
      this.timeEventResizes = new Map();
    }, 500);
  }

  /**
   * @hidden
   */
  allDayEventResizeStarted(
    allDayEventsContainer: HTMLElement,
    allDayEvent: WeekViewAllDayAppointment,
    resizeEvent: ResizeEvent
  ): void {
    this.allDayEventResizes.set(allDayEvent, {
      originalOffset: allDayEvent.offset,
      originalSpan: allDayEvent.span,
      edge: typeof resizeEvent.edges.left !== "undefined" ? "left" : "right"
    });
    this.resizeStarted(
      allDayEventsContainer,
      this.getDayColumnWidth(allDayEventsContainer)
    );
  }

  checkCallData(timeEvent) {
    console.log("allDayEventResizes", this.allDayEventResizes, timeEvent, this.validateDrag);
  }

  /**
   * @hidden
   */
  allDayEventResizing(
    allDayEvent: WeekViewAllDayAppointment,
    resizeEvent: ResizeEvent,
    dayWidth: number
  ): void {
    const currentResize: WeekViewAllDayEventResize = this.allDayEventResizes.get(
      allDayEvent
    );

    if (typeof resizeEvent.edges.left !== "undefined") {
      const diff: number = Math.round(+resizeEvent.edges.left / dayWidth);
      allDayEvent.offset = currentResize.originalOffset + diff;
      allDayEvent.span = currentResize.originalSpan - diff;
    } else if (typeof resizeEvent.edges.right !== "undefined") {
      const diff: number = Math.round(+resizeEvent.edges.right / dayWidth);
      allDayEvent.span = currentResize.originalSpan + diff;
    }
  }

  /**
   * @hidden
   */
  allDayEventResizeEnded(allDayEvent: WeekViewAllDayAppointment): void {
    const currentResize: WeekViewAllDayEventResize = this.allDayEventResizes.get(
      allDayEvent
    );

    if (currentResize) {
      const allDayEventResizingBeforeStart = currentResize.edge === "left";
      let daysDiff: number;
      if (allDayEventResizingBeforeStart) {
        daysDiff = allDayEvent.offset - currentResize.originalOffset;
      } else {
        daysDiff = allDayEvent.span - currentResize.originalSpan;
      }

      allDayEvent.offset = currentResize.originalOffset;
      allDayEvent.span = currentResize.originalSpan;

      let newStart: Date = allDayEvent.event.start;
      let newEnd: Date = allDayEvent.event.end || allDayEvent.event.start;
      if (allDayEventResizingBeforeStart) {
        newStart = addDaysWithExclusions(
          this.dateAdapter,
          newStart,
          daysDiff,
          this.excludeDays
        );
      } else {
        newEnd = addDaysWithExclusions(
          this.dateAdapter,
          newEnd,
          daysDiff,
          this.excludeDays
        );
      }

      this.appointmentTimesChanged.emit({
        newStart,
        newEnd,
        event: allDayEvent.event,
        type: CalendarAppointmentTimesChangedEventType.Resize
      });
      this.allDayEventResizes.delete(allDayEvent);
    }
  }

  /**
   * @hidden
   */
  getDayColumnWidth(eventRowContainer: HTMLElement): number {
    return Math.floor(eventRowContainer.offsetWidth / this.days.length);
  }

  /**
   * @hidden
   */
  eventDropped(
    dropEvent: DropEvent<{ event?: CalendarEvent; calendarId?: symbol }>,
    date: Date,
    allDay: boolean
  ): void {
    if (shouldFireDroppedEvent(dropEvent, date, allDay, this.calendarId)) {
      this.appointmentTimesChanged.emit({
        type: CalendarAppointmentTimesChangedEventType.Drop,
        event: dropEvent.dropData.event,
        newStart: date,
        allDay
      });
    }
  }

  /**
   * @hidden
   */
  dragStarted(
    eventsContainer: HTMLElement,
    event: HTMLElement,
    dayEvent?: DayViewEvent
  ): void {
    this.dayColumnWidth = this.getDayColumnWidth(eventsContainer);
    const dragHelper: CalendarDragHelper = new CalendarDragHelper(
      eventsContainer,
      event
    );
    this.validateDrag = ({ x, y, transform }) =>
      this.allDayEventResizes.size === 0 &&
      this.timeEventResizes.size === 0 &&
      dragHelper.validateDrag({
        x,
        y,
        snapDraggedEvents: this.snapDraggedEvents,
        dragAlreadyMoved: this.dragAlreadyMoved,
        transform
      });
    this.dragActive = true;
    this.dragAlreadyMoved = false;
    this.eventDragEnter = 0;
    if (!this.snapDraggedEvents && dayEvent) {
      this.view.hourColumns.forEach(column => {
        const linkedEvent = column.events.find(
          columnEvent =>
            columnEvent.event === dayEvent.event && columnEvent !== dayEvent
        );
        // hide any linked events while dragging
        if (linkedEvent) {
          linkedEvent.width = 0;
          linkedEvent.height = 0;
        }
      });
    }
    this.cdr.markForCheck();
  }

  /**
   * @hidden
   */
  dragMove(dayEvent: DayViewEvent, dragEvent: DragMoveEvent) {
    if (this.snapDraggedEvents) {
      const newEventTimes = this.getDragMovedEventTimes(
        dayEvent,
        dragEvent,
        this.dayColumnWidth,
        true
      );
      const originalEvent = dayEvent.event;
      const adjustedEvent = { ...originalEvent, ...newEventTimes };
      const tempEvents = this.events.map(event => {
        if (event === originalEvent) {
          return adjustedEvent;
        }
        return event;
      });
      this.restoreOriginalEvents(
        tempEvents,
        new Map([[adjustedEvent, originalEvent]])
      );
    }
    this.dragAlreadyMoved = true;
  }

  /**
   * @hidden
   */
  allDayEventDragMove() {
    this.dragAlreadyMoved = true;
  }

  /**
   * @hidden
   */
  dragEnded(
    weekEvent: WeekViewAllDayAppointment | DayViewEvent,
    dragEndEvent: DragEndEvent,
    dayWidth: number,
    useY = false
  ): void {
    this.view = this.getWeekView(this.events);
    this.dragActive = false;
    const { start, end } = this.getDragMovedEventTimes(
      weekEvent,
      dragEndEvent,
      dayWidth,
      useY
    );
    if (
      this.eventDragEnter > 0 &&
      isDraggedWithinPeriod(start, end, this.view.period)
    ) {
      this.appointmentTimesChanged.emit({
        newStart: start,
        newEnd: end,
        event: weekEvent.event,
        type: CalendarAppointmentTimesChangedEventType.Drag,
        allDay: !useY
      });
    }
  }

  handleAppointmentClicked(event, isSelecting: boolean) {
    console.log("handleAppointmentClicked event", event);
    this.eventRefresh.next({ event: event, isSelecting: isSelecting });
    this.eventClicked.emit({ event: event, isSelecting: isSelecting });
  }

  onViewSwipe(event) {
    console.log("onViewSwipe data", event);
    if (this.isMobileScreen) {
      this.onViewSwiped.emit(event.type);
    }
  }

  isToday(date: Date): boolean {
    const today = new Date();

    return this.dateAdapter.isSameYear(date, today)
      && this.dateAdapter.isSameMonth(date, today)
      && this.dateAdapter.isSameDay(date, today);
  }

  isWeekend(date: Date): boolean {
    return date.getDay() === 6 || date.getDay() === 0;
  }

  protected refreshHeader(): void {
    this.days = this.utils.getWeekViewHeader({
      viewDate: this.viewDate,
      weekStartsOn: this.weekStartsOn,
      excluded: this.excludeDays,
      weekendDays: this.weekendDays,
      ...getWeekViewPeriod(
        this.dateAdapter,
        this.viewDate,
        this.weekStartsOn,
        this.excludeDays,
        this.daysInWeek
      )
    });
  }
  protected refreshBody(): void {
    console.log("refreshBody", this.view);
    this.view = this.getWeekView(this.events);
     if (this.currentDayView === 1 && !this.callScrollTotimerOneday) {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          this.callScrollTotimerOneday = true;
           CalenderUtils.calendarTimeLineScrollForDayOne();
        }, 350);
     }

  }

  protected refreshAll(): void {
    this.refreshHeader();
    this.refreshBody();
    this.emitBeforeViewRender();
    this.getDataDisplayView();
    setTimeout(() => {
      this.newAllEventFromView();
    }, 250);
  }

  protected emitBeforeViewRender(): void {
    if (this.days && this.view) {
      this.beforeViewRender.emit({
        header: this.days,
        ...this.view
      });
    }
  }

  protected getWeekView(events: CalendarEvent[]) {
    return this.utils.getWeekView({
      events,
      viewDate: this.viewDate,
      weekStartsOn: this.weekStartsOn,
      excluded: this.excludeDays,
      precision: this.precision,
      absolutePositionedEvents: true,
      hourSegments: this.hourSegments,
      dayStart: {
        hour: this.dayStartHour,
        minute: this.dayStartMinute
      },
      dayEnd: {
        hour: this.dayEndHour,
        minute: this.dayEndMinute
      },
      segmentHeight: this.hourSegmentHeight,
      weekendDays: this.weekendDays,
      ...getWeekViewPeriod(
        this.dateAdapter,
        this.viewDate,
        this.weekStartsOn,
        this.excludeDays,
        this.daysInWeek
      )
    });
  }

  protected getDragMovedEventTimes(
    weekEvent: WeekViewAllDayAppointment | DayViewEvent,
    dragEndEvent: DragEndEvent | DragMoveEvent,
    dayWidth: number,
    useY: boolean
  ) {
    const daysDragged = roundToNearest(dragEndEvent.x, dayWidth) / dayWidth;
    const minutesMoved = useY
      ? getMinutesMoved(
        dragEndEvent.y,
        this.hourSegments,
        this.hourSegmentHeight,
        this.eventSnapSize
      )
      : 0;

    const start = this.dateAdapter.addMinutes(
      addDaysWithExclusions(
        this.dateAdapter,
        weekEvent.event.start,
        daysDragged,
        this.excludeDays
      ),
      minutesMoved
    );
    let end: Date;
    if (weekEvent.event.end) {
      end = this.dateAdapter.addMinutes(
        addDaysWithExclusions(
          this.dateAdapter,
          weekEvent.event.end,
          daysDragged,
          this.excludeDays
        ),
        minutesMoved
      );
    }

    return { start, end };
  }

  protected restoreOriginalEvents(
    tempEvents: CalendarEvent[],
    adjustedEvents: Map<CalendarEvent, CalendarEvent>
  ) {
    const previousView = this.view;
    this.view = this.getWeekView(tempEvents);
    const adjustedEventsArray = tempEvents.filter(event =>
      adjustedEvents.has(event)
    );
    this.view.hourColumns.forEach((column, columnIndex) => {
      previousView.hourColumns[columnIndex].hours.forEach((hour, hourIndex) => {
        hour.segments.forEach((segment, segmentIndex) => {
          column.hours[hourIndex].segments[segmentIndex].cssClass =
            segment.cssClass;
        });
      });
      adjustedEventsArray.forEach(adjustedEvent => {
        const originalEvent = adjustedEvents.get(adjustedEvent);
        const existingColumnEvent = column.events.find(
          columnEvent => columnEvent.event === adjustedEvent
        );
        if (existingColumnEvent) {
          // restore the original event so trackBy kicks in and the dom isn"t changed
          existingColumnEvent.event = originalEvent;
        } else {
          // add a dummy event to the drop so if the event was removed from the original column the drag doesn"t end early
          column.events.push({
            event: originalEvent,
            left: 0,
            top: 0,
            height: 0,
            width: 0,
            startsBeforeDay: false,
            endsAfterDay: false
          });
        }
      });
    });
    adjustedEvents.clear();
  }

  protected getTimeEventResizedDates(
    calendarEvent: CalendarEvent,
    resizeEvent: ResizeEvent
  ) {
    const minimumEventHeight = getMinimumEventHeightInMinutes(
      this.hourSegments,
      this.hourSegmentHeight
    );
    const newEventDates = {
      start: calendarEvent.start,
      end: getDefaultEventEnd(
        this.dateAdapter,
        calendarEvent,
        minimumEventHeight
      )
    };
    const { end, ...eventWithoutEnd } = calendarEvent;
    const smallestResizes = {
      start: this.dateAdapter.addMinutes(
        newEventDates.end,
        minimumEventHeight * -1
      ),
      end: getDefaultEventEnd(
        this.dateAdapter,
        eventWithoutEnd,
        minimumEventHeight
      )
    };

    if (typeof resizeEvent.edges.left !== "undefined") {
      const daysDiff = Math.round(
        +resizeEvent.edges.left / this.dayColumnWidth
      );
      const newStart = addDaysWithExclusions(
        this.dateAdapter,
        newEventDates.start,
        daysDiff,
        this.excludeDays
      );
      if (newStart < smallestResizes.start) {
        newEventDates.start = newStart;
      } else {
        newEventDates.start = smallestResizes.start;
      }
    } else if (typeof resizeEvent.edges.right !== "undefined") {
      const daysDiff = Math.round(
        +resizeEvent.edges.right / this.dayColumnWidth
      );
      const newEnd = addDaysWithExclusions(
        this.dateAdapter,
        newEventDates.end,
        daysDiff,
        this.excludeDays
      );
      if (newEnd > smallestResizes.end) {
        newEventDates.end = newEnd;
      } else {
        newEventDates.end = smallestResizes.end;
      }
    }

    if (typeof resizeEvent.edges.top !== "undefined") {
      const minutesMoved = getMinutesMoved(
        resizeEvent.edges.top as number,
        this.hourSegments,
        this.hourSegmentHeight,
        this.eventSnapSize
      );
      const newStart = this.dateAdapter.addMinutes(
        newEventDates.start,
        minutesMoved
      );
      if (newStart < smallestResizes.start) {
        newEventDates.start = newStart;
      } else {
        newEventDates.start = smallestResizes.start;
      }
    } else if (typeof resizeEvent.edges.bottom !== "undefined") {
      const minutesMoved = getMinutesMoved(
        resizeEvent.edges.bottom as number,
        this.hourSegments,
        this.hourSegmentHeight,
        this.eventSnapSize
      );
      const newEnd = this.dateAdapter.addMinutes(
        newEventDates.end,
        minutesMoved
      );
      if (newEnd > smallestResizes.end) {
        newEventDates.end = newEnd;
      } else {
        newEventDates.end = smallestResizes.end;
      }
    }

    return newEventDates;
  }

  handleDoubleClickedHandled(ev): void {
    this.handleDoubleClicked.emit({event: ev});
  }

  ngAfterViewInit(): void {
     if (window.matchMedia("(max-width: 599px)").matches) {
      this.attachSwipeWhenReady("#weekViewCalTimeEvents");
      this.attachSwipeWhenReady("#weekViewCalTimeEvents3Day");
      this.attachSwipeWhenReady("#weekViewCalTimeEventsD1");
    }

      CalenderUtils.calendarTimeLineScroll();
      if (this.mydiv && this.mydiv.nativeElement) {
          this.dragElement(this.mydiv.nativeElement);

      }

    setTimeout(() => {
          this.timelineInterval = timer(0, 2000).subscribe(result => {
          const calTimelinePosition: any = this.calendarRepository.getCalendarTimelinePosition(this.hourSegmentHeight);
          if (this.weekCalTimeline) {
            if (this.calendarRepository.daysInWeek === 1) {
              this.reCalculateTimeShow();
              setTimeout(() => {
                  this.refreshAll();
                  this.reCalculateTimeShow();
              }, 450);
            } else {
              this.weekCalTimeline.nativeElement.style.top = calTimelinePosition.position + "px";
            }
          }
        });
    }, 100);


  }

  checkWidthData(index, totalEvent) {
    if (this.calendarRepository.daysInWeek === 1) {
      let widthInPercentage: number = 0;
      if (totalEvent[index].left === 0) {
        widthInPercentage = 100;
      } else if (totalEvent[index].left > 0) {
        let anotherLeft = totalEvent[index].left / 2;
        widthInPercentage = 100 - anotherLeft;
      }
      return widthInPercentage;
    } else {
      return totalEvent[index].width;
    }

  }

  checkLeftData(index, totalEvent) {
    if (this.calendarRepository.daysInWeek === 1) {
      let leftInPercentage: number = 0;
      if (totalEvent[index].left === 0) {
        leftInPercentage = 0;
      } else if (totalEvent[index].left === 50) {
        let anotherLeft = totalEvent[index].left + (totalEvent[index].left / 2);
        leftInPercentage = 100 - anotherLeft;
      } else {
        leftInPercentage = totalEvent[index].left;
      }
      return leftInPercentage;
    } else {
      return totalEvent[index].left;
    }
  }


getTimeEventTop(top, length) {
  if (this.calendarRepository.daysInWeek === 1) {
    return  top + (length * 168);
  } else {
    return  top;
  }
}

// new day view custom start
getDataDisplayView() {

  this.getSameTimeEvents();
}

getSameTimeEvents() {
  this.sameTimeEvents = [];
  let events = this.view.hourColumns[0].events;
  events.map((ev, index) => {
      let time = this.getHourFromDateString(ev.event.start);
      let currentDate = new Date(this.viewDate).getDate();
      let date = new Date(ev.event.start);
      let eventDate = date.getDate();

      if (eventDate === currentDate) {
          this.getSameTimeEventsCount(time, ev);
      }
  });

  this.getNewTop();
  this.calculateExtraCellAddInBottom();

}

  checkIsSameStartDate(date) {
    let viewDate = new Date(this.viewDate);
    let start = new Date(date);
    if (viewDate.getDate() === start.getDate()) {
      return true;
    }
    return false;
  }


calculateExtraCellAddInBottom() {
  this.extraCellAddInBottom = [];
  let events = this.view.hourColumns[0].events;
  events.map((ev, index) => {
    if (ev["count"] !== 0) {
      this.extraCellAddInBottom.push("count");
    }
  });

}

getNewTop() {
  let events = this.view.hourColumns[0].events;
  let count = 0;
  let length = this.view.allDayEventRows.length;
  events.map((ev, index) => {
    let time = this.getHourFromDateString(ev.event.start);
      if (index === 0) {
        ev["newTop"] = (time * 168) + (length * 168);
      } else {
        if (ev["count"]) {
          count = count + 1;
        }
        ev["newTop"] = (time * 168) + (length * 168) + (count * 168);
      }
  });
}

getNTop(index) {
  if (this.calendarRepository.daysInWeek === 1) {
    let events = this.view.hourColumns[0].events;
    this.getDataDisplayView();
    // console.log("getDataDisplay view sameTimeEvents Top value:",   events[index]);
    return events[index]["newTop"];
  }

}

getTimeEventTopNew(top, length, timeEvent, indexItem) {
  setTimeout(() => {
    let time = this.getHourFromDateString(timeEvent.event.start);
    let topValue = 0;
    topValue = (time * 168);
    let count = 0;
    if (this.calendarRepository.daysInWeek === 1) {
      if (indexItem === 0) {
        timeEvent["newTop"] =  topValue + (length * 168);
      } else {
        let events = this.view.hourColumns[0].events;
        events.map((ev, index) => {
          if (indexItem <= index) {
            if (ev && ev["count"]) {
              count = count + ev["count"];
            }
          }
        });
        timeEvent["newTop"] = topValue + (length * 168) + (count * 168);
      }
    }
  }, 200);

}
getSameTimeEventsCount(hrs, ev) {

  if (this.sameTimeEvents.length === 0) {
    ev["count"] = 0;
    this.sameTimeEvents.push({time: hrs, count: 1});
  } else if (this.sameTimeEvents.length > 0) {
    let isExist = false;
    this.sameTimeEvents.map((hr) => {
      if (hr.time === hrs) {
        isExist = true;
        ev.count = hr.count;
        hr.count = hr.count + 1;
      }
    });
    if (!isExist) {
      this.sameTimeEvents.push({time: hrs, count: 1});
      ev["count"] = 0;
    }
  }
}

getDataToLoadMoreRow(index) {
  let arrayToRender = [];
  this.sameTimeEvents.map((ev) => {
    if (ev.time === index) {
      if (ev.count > 1) {
        for (let i = 1 ; i < ev.count; i++) {
          arrayToRender.push(i);
        }
      }
    }
  });
  // console.log("getDataToLoadMoreRow", index, arrayToRender);

  return arrayToRender;
}
getEventDataWhichLessWidth(event: Array<any> = []) {
  this.moreHrs = [];
    event.forEach((ev, index) => {
      if (ev.width === 50) {
        let hrs = this.getHourFromDateString(ev.event.start);
        this.moreHrs.push({time: hrs, count: 1});
      }
      if (ev.width === 33.333333333333336) {
        let hrs = this.getHourFromDateString(ev.event.start);
        this.moreHrs.push({time: hrs, count: 2});
      }
    });

    // console.log("event less this.moreHrs", event, this.moreHrs);
}

checkTimeCount(date) {
  let hrs = this.getHourFromDateString(date);
  let dateCount = 0;
  let rowCombo = [];
  this.moreHrs.map((hr) => {
    if (hr.time === hrs) {
      dateCount = dateCount + 1;
    }
  });
  if (dateCount > 0) {
    if (dateCount === 2) {
      rowCombo = [1];
    }
    if (dateCount === 3) {
      rowCombo = [1, 2];
    }
  }
  return rowCombo;
}

isfifty(date) {
  let hrs = this.getHourFromDateString(date);
  let isFifty = false;
  this.moreHrs.map((hr) => {
    if (hr.time === hrs && hr.count === 1) {
      isFifty = true;
    }
  });
  return isFifty;
}

isthirtyThree(date) {
  let hrs = this.getHourFromDateString(date);
  let isThirtyThree = false;
  this.moreHrs.map((hr) => {
    if (hr.time === hrs && hr.count === 2) {
      isThirtyThree = true;
    }
  });
  return isThirtyThree;
}

getHourFromDateString(dateString) {
  let date = new Date(dateString);
  return date.getHours();
}

findOverlappingSlots(appointments) {
  // Convert event start and end times to Date objects
  appointments.forEach(app => {
      app.start = new Date(app.event.start);
      app.end = new Date(app.event.end);
  });

  // Sort appointments by start time
  appointments.sort((a, b) => a.start - b.start);

  let overlappingSlots = [];
  let currentSlot = appointments[0];

  for (let i = 1; i < appointments.length; i++) {
      let nextSlot = appointments[i];

      // Check if current slot overlaps with the next one
      if (currentSlot.end > nextSlot.start) {
          overlappingSlots.push({
              start: nextSlot.start,
              end: currentSlot.end < nextSlot.end ? currentSlot.end : nextSlot.end,
              appointments: [currentSlot, nextSlot]
          });
          // Merge the intervals
          currentSlot.end = new Date(Math.max(currentSlot.end, nextSlot.end));
      } else {
          currentSlot = nextSlot;
      }
  }

  // Return overlapping time slots with the number of overlapping events
  return overlappingSlots.map(slot => ({
      start: slot.start,
      end: slot.end,
      startDateTime: this.formatDateTime(slot.start),
      endDateTime: this.formatDateTime(slot.end),
      overlappingEvents: slot.appointments.length
  }));
}

formatDateTime(dateString) {
  let date = new Date(dateString);

  // Extracting day, hours, and minutes in local time
  let day = String(date.getDate()).padStart(2, "0");
  let hours = String(date.getHours()).padStart(2, "0");
  let minutes = String(date.getMinutes()).padStart(2, "0");

  // Format as DD HH:ss
  return `${day} ${hours}:${minutes}`;
}

reCalculateTimeShow() {
  const now = moment();
  let hour = now.hour();
  let extraSegment = 0;

  this.sameTimeEvents.map((ev) => {
    if (ev.time < hour) {
      extraSegment = extraSegment + (ev.count - 1);
    }
  });

  const calTimelinePosition: any = this.calendarRepository.getCalendarTimelinePosition(42);
  if (this.weekCalTimeline1Day) {
    if (this.calendarRepository.daysInWeek === 1) {
      if (this.view.allDayEventRows.length > 0) {
        this.weekCalTimeline1Day.nativeElement.style.top = (extraSegment * 168) + calTimelinePosition.position + (this.view.allDayEventRows.length * 168) + "px";
      } else {
        this.weekCalTimeline1Day.nativeElement.style.top = (extraSegment * 168) + calTimelinePosition.position + "px";
      }
    }
  }
  console.log("reCalculateTimeShow heigh", ((extraSegment * 168) + calTimelinePosition.position + (this.view.allDayEventRows.length * 168)), now.hour(), extraSegment);
}
  getSetWidth() {
    const mainDiv = document.getElementById("calendarContainer");
    if (mainDiv) {
      const wid = mainDiv.clientWidth;
      const leftoverWidth = Math.floor(wid - 52.5);
      const cellWidth = Math.floor(leftoverWidth / 3);
      this.customCellWidth = cellWidth;
      let customCell: any = [];
      customCell  = document.getElementsByClassName("cal-new-cell");
      if (customCell && customCell.length && customCell.length > 0) {
          for (let i = 0; i < customCell.length; i++) {
            customCell[i].style.width = cellWidth + "px";
            console.log("customCell[i]", i, customCell[i].style.width , cellWidth);
            this.cellWidth3dayVeiwMobile = cellWidth;
          }
      }
      this.cdr.markForCheck();
    } else {
      this.getSetWidth();
    }
  }

  newAllEventFromView() {
    this.allEventFromView = [];
    const seenEventIds = new Set<string>();
         this.view.hourColumns?.forEach(hrEv => {
          hrEv.events?.forEach((inEv: any) => {
            const eventId = inEv.eventId ?? inEv.event?.id;
            if (eventId && !seenEventIds.has(eventId)) {
              seenEventIds.add(eventId);
              this.allEventFromView.push(inEv.event);
            }
          });
        });
   console.log("Hour columns inside timeout:", this.view.hourColumns, this.allEventFromView); // ✅ Check if data is present
  }

  initCustomeAppointments() {
    console.trace("initCustomeAppointments trace");
    this.appointments = [];
    if (this.events.length === 0) {
      setTimeout(() => {
          this.initCustomeAppointments();
      }, 2500);
      return;
    }


    // this.events.map(event => ({
    //   title: event.title,
    //   start: this.parseISO(toString(event.start)),
    //   end: event.end ? this.parseISO(event.end) : undefined,
    //   color: event.color || '#42a5f6' // Default color if not specified,
    // }));


    this.events.map(event => ({
      title: event.title,
      start: event.start,
      end: event.end ? event.end : undefined,
      color: event.color || "#42a5f6" // Default color if not specified,
    }));

    console.log("custom events 3 day", this.events);
     const overlapMap: Record<string, any[]> = {};
     const CELL_HEIGHT = 40;
     const HEADER_HEIGHT = 41;
     const TOTAL_CALENDAR_HEIGHT = 3880;
     const CELL_WIDTH = this.customCellWidth;
     const GAP_LEFT = 5;

    //  this.events.forEach((event, index) => {
    if (this.isNewInMobile && this.isNewInMobile.id) {
      let alreadyNewhave: boolean = false;
      this.allEventFromView.map(alEv => {
        if (alEv.id === 1000111) {
          alreadyNewhave = true;
          alEv.start = this.isNewInMobile.start;
          alEv.end = this.isNewInMobile.end;
        }
      });

      if (!alreadyNewhave) {
         this.allEventFromView.push(this.isNewInMobile);
      }

    }
    this.allEventFromView.forEach((event, index) => {
      //  const start = this.parseISO(event.start);
      //  const end = this.parseISO(event.end);

       const start = event.start;
       const end = event.end;

       // ✅ Check if this event is inside the current visible days
       let insideDate = false;
       this.daysCustom.forEach(dt => {
         const sameStart = this.isSameDate(start, dt);
         const sameEnd = this.isSameDate(end, dt);
         if (sameStart || sameEnd) insideDate = true;
       });

       if (!insideDate) return;

       const slots = this.getAppointmentDurationInSlots({ start, end });
       const offset = this.getSlotOffsetFromDayStart(start, 0);


       console.log("check the data for isRepeatAppt: event.isRepeatAppt,", event, event.isRepeatAppt, this.view);

      const data  = {
          title: event.title,
          start,
          end,
          color: this.getBackgroundColor(event.bgcolor || "#42a5f6"),
          index: index + 1,
          height: slots * CELL_HEIGHT,
          top: offset * CELL_HEIGHT + HEADER_HEIGHT,
          eventId: event.eventId,
          ptst: event.ptst,

          alarm: event.alarm,
          alarmData: event.alarmData,
          allDay: event.allDay,
          bgcolor: event.bgcolor,
          class: event.class,
          description: event.description,
          draggable: event.draggable,
          duration: event.duration,
          ex: event.ex,
          f: event.f,
          fb: event.fb,
          fba: event.fba,
          folderId: event.folderId,
          hasEx: event.hasEx,
          id: event.id,
          inst: event.inst,
          invId: event.invId,
          isOrganizer: event.isOrganizer,
          isRepeatAppt: (event && event?.isRepeatAppt) ? event.isRepeatAppt : undefined,
          location: event.location,
          ms: event.ms,
          neverSent: event.neverSent,
          otherAtt: event.otherAtt,
          recur: event.recur,
          rev: event.rev,
          s: event.s,
          t: event.t,
          tn: event.tn
       };

       const diff = data.height + data.top;
       if (diff > TOTAL_CALENDAR_HEIGHT) {
         data.height = TOTAL_CALENDAR_HEIGHT - data.top;
       }

       // ✅ Group key includes date and offset to prevent cross-day overlaps
       const groupKey = format(start, "yyyy-MM-dd") + "-" + offset;
       if (!overlapMap[groupKey]) overlapMap[groupKey] = [];
       overlapMap[groupKey].push(data);
     });

     // ✅ Apply layout with spacing and proper left/width
     for (const group of Object.values(overlapMap)) {
       const count = group.length;
       const totalGap = (count - 1) * GAP_LEFT;
       const widthPerAppt = (CELL_WIDTH - totalGap) / count;


       group.forEach((appt, i) => {
         if (group.length > 1) {
           appt["groupIndex"] = i + 1;
         }
         let left = this.setLeft(appt);
         left = left + i;
         left = left + i * (widthPerAppt + GAP_LEFT);
         appt.width = widthPerAppt;
         appt.left = left;

         this.appointments.push(appt);
       });
     }

       this.checkAndAssignOverLapAppointments();
        this.checkSameApptForDiffCalendar();
          this.day3VisiblityAftersetPosition = true;
          setTimeout(() => {
           this.cdr.markForCheck();

          }, 500);
    //  if (this.startDate !== this.daysCustom[0].getDate()) {
    //     clearTimeout(this.debounceTimerFor3dayView);
    //     this.day3VisiblityAftersetPosition = false;
    //  } else {
    //     this.checkAndAssignOverLapAppointments();
    //     this.checkSameApptForDiffCalendar();
    //  }

    // this.debounceTimerFor3dayView = setTimeout(() => {
    //   const date = this.daysCustom[0].getDate();
    //   this.startDate = date;
    //   this.checkAndAssignOverLapAppointments();
    //   this.checkSameApptForDiffCalendar();

    //   this.cdr.markForCheck();
    // }, 500);


    //  setTimeout(() => {
    //   this.checkAndAssignOverLapAppointments();
    //   setTimeout(() => {
    //     // this.getSetWidth();
    //      this.debounceTimerFor3dayView = setTimeout(() => {
    //       const date = this.daysCustom[0].getDate();
    //      this.startDate = date;
    //      this.day3VisiblityAftersetPosition = true;
    //      this.cdr.markForCheck();
    //     }, 1500);
    //     console.log("Hour columns inside timeout data appointment", this.appointments);
    //   }, 200);
    //  }, 1200);
  }


  checkAndAssignOverLapAppointments() {
    this.appointments.forEach((appt: any) => {
      const overLapAppointments = this.getAppointmentsSharingEarlierTime(this.appointments, appt);
      if (overLapAppointments && overLapAppointments.length && overLapAppointments.length > 0) {
        let leftAdd = overLapAppointments.length * 4;
        appt.left = appt.left + leftAdd;
        if (appt && appt.groupIndex) {
          if (appt.groupIndex === 1) {
            if (appt.day === 1) {
              appt.left = 52.5 + 4;
            } else if (appt.day === 2) {
              appt.left = (52.5 + this.customCellWidth) + 4;
            } else if (appt.day === 3) {
              appt.left = (52.5 + (this.customCellWidth * 2)) + 4 ;
            }
          } else {
             if (appt.day === 1) {
              appt.left = 52.5 + 4 + ((appt.groupIndex - 1) * appt.width);
              } else if (appt.day === 2) {
                appt.left = (52.5 + this.customCellWidth) + 4 + ((appt.groupIndex - 1) * appt.width);
              } else if (appt.day === 3) {
                appt.left = (52.5 + (this.customCellWidth * 2)) + 4 + ((appt.groupIndex - 1) * appt.width);
              }
          }
        } else {
          appt.width = appt.width - leftAdd;
        }
        // appt.width = appt.width - leftAdd;
      }
      appt["ovelapAppointments"] = overLapAppointments;
    });

    this.cdr.markForCheck();
  }

  isSameDate(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  setLeft(appt: any) {
      let left = 0;
      const apptDate = new Date(appt.start);
      const day = apptDate.getDate();
      this.daysCustom.forEach((dy, index) => {
          const datte = dy.getDate();
          console.log("appt datte", datte);
          if (day === datte) {
            if (index === 0) {
                left = 52.5;
                appt.day = 1;
            } else if (index === 1) {
              left = (52.5 + this.customCellWidth) ;
              appt.day = 2;
            } else if (index === 2) {
              left = (52.5 + (this.customCellWidth * 2)) ;
              appt.day = 3;
               console.log("appt datte", appt, 3);
            }

          }
      });

      const overLapCount = this.countAppointmentsBeforeStart(this.appointments, appt);
      appt["ovelap"] = overLapCount;
      return left;
  }

getAppointmentsSharingEarlierTime(appointments: any[], currentEvent: any): any[] {
  const currentStart = new Date(currentEvent.start);

  const shared: any[] = [];

  for (const appt of appointments) {
    if (appt === currentEvent) continue;

    const apptStart = new Date(appt.start);
    const apptEnd = new Date(appt.end);

    const sharesEarlierTime =
      apptStart <= currentStart && apptEnd > currentStart;

    if (sharesEarlierTime) {
      shared.push(appt);
    }
  }

  return shared;
}


  countAppointmentsBeforeStart(appointments: any[], currentEvent: any): number {
    const currentStart = new Date(currentEvent.start);
    const currentEnd = new Date(currentEvent.end);
    let count = 0;

    for (const appt of appointments) {
      if (appt === currentEvent) break;

      const apptStart = new Date(appt.start);

      const sameStartDate = isSameDay(apptStart, currentStart);
      const sameEndDate = isSameDay(apptStart, currentEnd); // in case you want to match end too

      if ((sameStartDate || sameEndDate) && apptStart < currentStart) {
        count++;
      }
    }

    return count;
  }

  isOverlapping(start1: Date, end1: Date, start2: Date, end2: Date): boolean {
    return start1 < end2 && start2 < end1;
  }

  checkifHighestAppointment(app: any) {
    const heightApp = app.height || 40;
    let isHighest = true;
    this.appointments.forEach((a: any) => {
      if (a.height > heightApp) {
        isHighest = false;
      }
    });

    if (isHighest) {

      this.appointments.forEach((a: any) => {
        if (a.index === 1) {
          a.index = app.index;
        }
      });

       app.index = 1;
    }

    this.checkAnotherAppointmentInSameTime(app);
    return app.index;
  }

  getLeftPosition(left: number): number {

    return left + 52; // adds the time label width
  }

  checkAnotherAppointmentInSameTime(app: any) {
    const start = app.start;
    const end = app.end;

    let withinInterval: any = [];
    for (const appointment of this.appointments) {
      if (appointment.index !== app.index && this.isWithinInterval(start, { start: appointment.start, end: appointment.end }) ||
          this.isWithinInterval(end, { start: appointment.start, end: appointment.end })) {
          withinInterval.push(appointment);
      }
    }

    console.log("No other appointments in the same time range", app , withinInterval);
  }

  appSetFront(app: any) {
    const index = app.index;
    app.index = 500;
    setTimeout(() => {
      app.index = index;
      console.log("appSetFront", app);
    }, 1200);
  }
  calculateAllDayEventFor3dayViewCustom() {
    this.allDayForCustomEvent3day.day1 = [];
    this.allDayForCustomEvent3day.day2 = [];
    this.allDayForCustomEvent3day.day3 = [];
    if (this.view && this.view.allDayEventRows && this.view.allDayEventRows.length > 0) {
      if (this.view.allDayEventRows[0] && this.view.allDayEventRows[0].row && this.view.allDayEventRows[0].row.length) {
        this.view.allDayEventRows.map(alEv => {
          alEv.row.map(allDayEv => {
              this.daysCustom.forEach((dt, index) => {
                if (index === 0) {
                  // const ddAAtta = this.checkAppointmentRange(allDayEv.event.start, allDayEv.event.end, this.daysCustom[index]);
                  // allDayEv.event["day1"] = ddAAtta;
                    if (allDayEv.offset === 0) {
                       allDayEv.event["offset"] = allDayEv.offset;
                       allDayEv.event["span"] = allDayEv.span;
                       this.allDayForCustomEvent3day.day1.push(allDayEv.event);
                       this.cdr.markForCheck();
                    }

                } else if (index === 1) {
                    // const ddAAtta = this.checkAppointmentRange(allDayEv.event.start, allDayEv.event.end, this.daysCustom[index]);
                    // allDayEv.event["day2"] = ddAAtta;

                    // if (ddAAtta && ddAAtta.inside && ddAAtta.inside === true) {
                      if (allDayEv.offset === 1) {
                          allDayEv.event["offset"] = allDayEv.offset;
                          allDayEv.event["span"] = allDayEv.span;
                          let count = 0;
                          if (this.allDayForCustomEvent3day.day1.length > 0) {
                            this.allDayForCustomEvent3day.day1.map(d1Ev => {
                              if (d1Ev.span > 1 ) {
                                count = count + 1;
                              }
                            });
                          }

                        allDayEv.event["count"] = count;
                        if (this.allDayForCustomEvent3day.day2.length >= count) {
                            allDayEv.event["count"] = count + this.allDayForCustomEvent3day.day2.length;
                        }
                        this.allDayForCustomEvent3day.day2.push(allDayEv.event);
                        this.cdr.markForCheck();

                      // }

                    }

                } else if (index === 2) {
                    // const ddAAtta = this.checkAppointmentRange(allDayEv.event.start, allDayEv.event.end, this.daysCustom[index]);
                    // allDayEv.event["day3"] = ddAAtta;
                    // if (ddAAtta && ddAAtta.inside && ddAAtta.inside === true) {
                      if (allDayEv.offset === 2) {
                          allDayEv.event["offset"] = allDayEv.offset;
                          allDayEv.event["span"] = allDayEv.span;
                          let count = 0;
                          if (this.allDayForCustomEvent3day.day1.length > 0) {
                            this.allDayForCustomEvent3day.day1.map(d1Ev => {
                              if (d1Ev.span > 2 ) {
                                count = count + 1;
                              }
                            });
                          }
                          if (this.allDayForCustomEvent3day.day2.length > 0) {
                            this.allDayForCustomEvent3day.day2.map(d2Ev => {
                              if (d2Ev.span > 1 ) {
                                count = count + 1;
                              }
                            });
                          }
                          if (this.allDayForCustomEvent3day.day3.length > 0) {
                            this.allDayForCustomEvent3day.day3.map(d3Ev => {
                              if (d3Ev.span > 0 ) {
                                count = count + 1;
                              }
                            });
                          }

                          allDayEv.event["count"] = count;
                          this.allDayForCustomEvent3day.day3.push(allDayEv.event);
                          this.cdr.markForCheck();
                      }

                    // }

                }
              // }
            });
          });
        });
      }

    console.log("calculateAllDayEventFor3dayViewCustom", this.allDayForCustomEvent3day, this.view.allDayEventRows);
    }

  }

  calculateAlldayEventWidht(dayIndex, currentAlldayEv) {
    setTimeout(() => {
        let apptWidth = 0;
        if (dayIndex === 0) {
          console.log("calculateAlldayEventWidht", this.customCellWidth, dayIndex, currentAlldayEv, currentAlldayEv.day1.futureDays);
          if (currentAlldayEv.day1.futureDays > 1 ) {
            apptWidth =  (this.customCellWidth * 3) - 6;
          } else if (currentAlldayEv.day1.futureDays === 1) {
            apptWidth =  (this.customCellWidth * 2) - 18;
          } else {
            apptWidth =  this.customCellWidth - 18;
          }
          currentAlldayEv.day1.alldayWidth = apptWidth;
          this.cdr.markForCheck();
          return apptWidth;
        } else if (dayIndex === 1) {
          console.log("calculateAlldayEventWidht", this.customCellWidth, dayIndex, currentAlldayEv, currentAlldayEv.day2.futureDays);
          if (currentAlldayEv.day2.futureDays > 0) {
            apptWidth =  (this.customCellWidth * 2) - 18;
          } else {
            apptWidth =  this.customCellWidth - 18;
          }
          currentAlldayEv.day2.alldayWidth = apptWidth;
          this.cdr.markForCheck();
          return apptWidth;
        } else if (dayIndex === 2) {
           apptWidth =  this.customCellWidth - 18;
           currentAlldayEv.day3.alldayWidth = apptWidth;
          this.cdr.markForCheck();
          return apptWidth;
        }

        this.cdr.markForCheck();
        return 250;
    }, 250);



  }


   checkAppointmentRange(start: Date, end: Date, check: Date) {
    const MS_PER_DAY = 86400000;

    // normalize all to start of day
    const s0 = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const e0 = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const c0 = new Date(check.getFullYear(), check.getMonth(), check.getDate());

    const inside = c0.getTime() >= s0.getTime() && c0.getTime() <= e0.getTime();
    const isAppointmentStart = c0.getTime() === s0.getTime();
    const isAppointmentInside = inside && !isAppointmentStart;

    const totalDays = Math.floor((e0.getTime() - s0.getTime()) / MS_PER_DAY) + 1;

    const futureDays = inside
      ? Math.max(0, Math.floor((e0.getTime() - c0.getTime()) / MS_PER_DAY))
      : 0;

    return { inside, isAppointmentStart, isAppointmentInside, totalDays, futureDays };
}

  onSlotClick(day: Date, time: string): void {
    console.log("Clicked slot:", day.toDateString(), time);

    // Add your booking logic here
  }

checkAppointmentForThisTimeSlot(day: Date, time: string, dayIndex: any): any[] {
  const slotAppointments = [];
  const slotDate = day.getDate();
  const slotMonth = day.getMonth();
  const slotYear = day.getFullYear();

  for (const appointment of this.appointments) {
    const apptDate = new Date(appointment.start);
    const apptTime = format(apptDate, "HH:mm");

    const sameDate =
      apptDate.getDate() === slotDate &&
      apptDate.getMonth() === slotMonth &&
      apptDate.getFullYear() === slotYear;


    if (sameDate && apptTime === time ) {
      slotAppointments.push(appointment);
    }

  }

  return slotAppointments;
}



  getAppointmentDurationInSlots(appt: { start: Date; end: Date }): number {
    const diffInMs = appt.end.getTime() - appt.start.getTime();
    const diffInMinutes = diffInMs / (1000 * 60);
    return Math.ceil(diffInMinutes / 15);
  }


 getSlotOffsetFromDayStart(apptStart: Date, calendarStartHour: number): number {
  const calendarStart = new Date(
    apptStart.getFullYear(),
    apptStart.getMonth(),
    apptStart.getDate(),
    calendarStartHour,
    0
  );

  const diffMinutes = (apptStart.getTime() - calendarStart.getTime()) / (1000 * 60);
  return Math.max(0, Math.floor(diffMinutes / 15)); // prevent negative slot offset
}

 getBackgroundColor(color: string): string {
      const grandient = CalenderUtils.getBackgroundGrandientColor(color);
      if (!!grandient) {
        return grandient;
      }
      return color;

  }

  private handleAppSelect(object: {appt: any, dayIndex: number}) {
    const appointment = this.view.hourColumns[object.dayIndex].events.find(ev => (ev.event["id"] === object.appt.id));
    if (!appointment) return;
    console.log("Clicked appointment", appointment);
    this.handleDoubleClickedHandled(appointment.event);
  }

   getTagColorByNames(names: string): string {
      let color = MailConstants.DEFAULT_COLOR;
      if (names) {
        const tagNames = names.split(",");
        const tags = this.allZimbraTags.filter(tt => tt.name.toLowerCase() === tagNames[0].toLowerCase());
        if (!!tags && !!tags[0]) {
          // console.log("getTagColorByNames tags: ", tags);
          const t = tags[0];
          if (t.color && MailConstants.COLOR_LIST[t.color]) {
            t.color = MailConstants.COLOR_LIST[t.color];
          }
          if (t.rgb) {
            t.rgb = t.rgb.toLowerCase();
            if (t.rgb === "#999") {
              t.rgb = "#999999";
            }
          }
          color = t.rgb || t.color || color;

        }
      }
      return color;
    }



  parseISO(dateString: string): Date {
    return new Date(dateString);
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  isWithinInterval(date: Date, interval: { start: Date; end: Date }): boolean {
    return date.getTime() >= interval.start.getTime() && date.getTime() <= interval.end.getTime();
  }



  selectAllEvent(appt) {
     appt["selected"] = true;
     this.handleDoubleClickedHandled(appt);
      setTimeout(() => {
        appt.selected = false;
        this.cdr.markForCheck();
      }, 750);
  }

  dragStartedCustom(appt, $event) {
    console.log("dragCustom data dragStartedCustom: ", appt, $event);
  }

  allDayEventDragMoveCustom(appt, $event) {
    console.log("dragCustom data  allDayEventDragMoveCustom: ", appt, $event);

  }
  dragEndedCustom(appt, $event) {
    console.log("dragCustom data  dragEndedCustom: ", appt, $event);

  }

  handleDragStart(appt: any, event: MouseEvent | TouchEvent) {

    if (event instanceof MouseEvent) {
      console.log(" dragCustom data Mouse drag started:", appt, event);
    } else {
      console.log("Touch drag started:", appt, event);
    }
  }

  onDragMove(event: DragEvent, appt: any) {
    console.log("dragCustom data Dragging:", appt, event);
  }

  onDragEnd(event: DragEvent, appt: any) {
    console.log("dragCustom data Drag ended:", appt, event);
  }

    dragElement(elmnt: HTMLElement) {
    let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
    const header = elmnt.querySelector("#mydivheader") as HTMLElement;

    if (header) {
      header.onmousedown = dragMouseDown;
    } else {
      elmnt.onmousedown = dragMouseDown;
    }

    const self = this;

    function dragMouseDown(e: MouseEvent) {
      e.preventDefault();
      pos3 = e.clientX;
      pos4 = e.clientY;
      document.onmouseup = closeDragElement;
      document.onmousemove = elementDrag;
    }

    function elementDrag(e: MouseEvent) {
      e.preventDefault();
      pos1 = pos3 - e.clientX;
      pos2 = pos4 - e.clientY;
      pos3 = e.clientX;
      pos4 = e.clientY;
      elmnt.style.top = (elmnt.offsetTop - pos2) + "px";
      elmnt.style.left = (elmnt.offsetLeft - pos1) + "px";
    }

    function closeDragElement() {
      document.onmouseup = null;
      document.onmousemove = null;
    }
  }

  onMouseDown(event: any, appt: any) {
    if (event.button === 2) {
      return; // Do not interfere with right-click (button 2 is for right-click)
    }
    this.isLongPress = false; // Reset long press flag
    this.pressTimer = setTimeout(() => {
      this.isLongPress = true;
       this.isMousePressed = true; // Mouse is now pressed
      this.startPosition = { x: event.clientX, y: event.clientY }; // Store starting position
      this.selectedAppointment = appt;
      const oldleft = this.selectedAppointment.left;
      const oldTop = this.selectedAppointment.top;

      this.oldAppointmentLeft = oldleft;
      this.oldAppointmentTop = oldTop;
      console.log("Mouse down at:", this.startPosition, this.selectedAppointment, this.oldAppointmentLeft, this.oldAppointmentTop);
      this.onLongPress(event);
    }, this.longPressThreshold);
  }

   onMouseMove(event: any) {
     console.log("touch test:>>", event );
    if (this.isMousePressed) { // Only track movement if the mouse is pressed
      this.currentPosition = { x: event.clientX, y: event.clientY };
      if (this.startPosition && this.startPosition.y && this.startPosition.x) {
        let diffY = this.currentPosition.y - this.startPosition.y;
        let diffX = this.currentPosition.x - this.startPosition.x;
        console.log("this.selectedAppointment left:", this.selectedAppointment.left , "top", this.selectedAppointment.top );

        console.log("Mouse moving to diffX:", diffX , "diffY", diffY );
        this.selectedAppointment.top = this.oldAppointmentTop + diffY;
        this.selectedAppointment.left = this.oldAppointmentLeft + diffX;

      }
      console.log("Mouse moving to:", this.currentPosition);
      // You can add custom logic for dragging here, e.g., move an element, etc.
    }
  }

  onMouseUp(event: any) {
    if (this.pressTimer) {
      clearTimeout(this.pressTimer);
      console.log("LongPress leave");
      this.isMousePressed = false;
       this.selectedAppointment.top = this.oldAppointmentTop;
        this.selectedAppointment.left = this.oldAppointmentLeft;
        setTimeout(() => {
          this.selectedAppointment = null;
        }, 250);

    }

    if (!this.isLongPress) {
      this.onClick(event); // Regular click behavior
    }
  }

  onMouseLeave(event: any) {
    // Handle the case when the mouse leaves the button before release
    if (this.pressTimer) {
      clearTimeout(this.pressTimer);
    }
  }

  onClick(event: any) {
    console.log("Short click detected!");
  }

  onLongPress(event: any) {
    console.log("Long press detected!");
  }

 // Handle pointer down event
  onPointerDown(event: PointerEvent, appt, bottom: boolean = false) {
    this.isPointerActive = true;
    this.selectedAppointment = appt;
    const oldTop = appt.top;
    const oldHeight = appt.height;

    this.oldAptTop = oldTop;
    this.oldAptHeight = oldHeight;
    this.startPositionPointer = { x: event.clientX, y: event.clientY };
    this.oldStartY =  event.clientY;
    console.log("pointer startPositionPointer", this.startPositionPointer, this.oldStartY, appt);
    if (bottom) {
      appt["pointerBottom"] = true;
    } else {
      appt["pointerUP"] = true;
    }
    event.preventDefault(); // Prevent default behavior like text selection
  }
  // Handle pointer move event
  onPointerMove(event: PointerEvent, bottom: boolean = false, appt: any) {
    if (bottom) {
      appt["pointerBottom"] = true;
    } else {
      appt["pointerUP"] = true;
    }
    if (!this.isPointerActive) return;
     if (this.timerPointerMoving) {
      clearTimeout(this.timerPointerMoving);
    }
    this.isDraggingCustomAppointment = true;

    let parent: any = document.getElementsByClassName("cal-flex-box-content");
    parent[0].style.overflow = "hidden";

    this.currentPositionPointer = { x: event.clientX, y: event.clientY };

    console.log("pointer movind", this.currentPositionPointer);

    const diff = this.oldStartY - this.currentPositionPointer.y;


    if (!bottom) {
      this.selectedAppointment.height = this.oldAptHeight + diff;
      this.selectedAppointment.top = this.oldAptTop - diff;
      this.topResizing = true;

    } else {
        this.selectedAppointment.height = this.oldAptHeight - diff;
        this.bottomResizeing = true;

    }


     this.timerPointerMoving = setTimeout(() => {
        let parent: any = document.getElementsByClassName("cal-flex-box-content");
        parent[0].style.overflow = "scroll";
        setTimeout(() => {
            console.log("this.selectedAppointment.height", this.oldStartY, this.currentPositionPointer.y, diff);
            this.selectedAppointment.height = this.oldAptHeight;
            this.selectedAppointment.top = this.oldAptTop;
            const startData =  new Date(this.selectedAppointment.start);
            const endData =  new Date(this.selectedAppointment.end);
            if (bottom) {
              if (diff > 0) {
                  let countCell = Math.ceil(diff / 40);
                  let timeRemove = countCell * 15;
                  endData.setMinutes(endData.getMinutes() - timeRemove);
              } else {
                let newDiff = (diff * (-1));
                let countCell = Math.ceil((newDiff) / 40);
                let timeRemove = countCell * 15;
                endData.setMinutes(endData.getMinutes() + timeRemove);
              }
            } else {
              if (diff > 0) {
                  let countCell = Math.ceil(diff / 40);
                  let timeRemove = countCell * 15;
                  startData.setMinutes(startData.getMinutes() - timeRemove);
              } else {
                  let newDiff = (diff * (-1));
                  let countCell = Math.ceil((newDiff) / 40);
                  let timeRemove = countCell * 15;
                 startData.setMinutes(startData.getMinutes() + timeRemove);
              }
            }


            // Store the new date
            const newEventDatesData = {
              start: startData,
              end: endData,
            };
            this.customResizeAppt(newEventDatesData, this.selectedAppointment);
            this.topResizing = false;
            this.bottomResizeing = false;
            this.cdr.markForCheck();
        }, 200);
    }, 800);

  }


  customResizeAppt(newEventDatesData, evnt) {
    console.log("");
     this.appointmentTimesChanged.emit({
        newStart: newEventDatesData.start,
        newEnd: newEventDatesData.end,
        event: evnt,
        type: CalendarAppointmentTimesChangedEventType.Resize
      });
      this.broadcaster.broadcast("appointmentTimeEnded", {
        newStart: newEventDatesData.start,
        newEnd: newEventDatesData.end,
        event: evnt,
        type: CalendarAppointmentTimesChangedEventType.Resize
      });
  }
  // Handle pointer up event
  onPointerUp(event: PointerEvent, appt, bottom: boolean = false) {
     appt["pointerBottom"] = false;
     appt["pointerUP"] = false;
    if (this.isPointerActive) {
      this.isPointerActive = false;
      console.log("Pointer released at:", event.clientX, event.clientY);
    }
    console.log("isPointerActive", appt);
  }

  // Handle pointer cancel event
  onPointerCancel(event: PointerEvent, appt, bottom: boolean = false) {
    appt["pointerBottom"] = false;
    appt["pointerUP"] = false;
    this.isPointerActive = false;
  }

   onContextMenu(event: MouseEvent) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      event.preventDefault(); // Prevent the default context menu (right-click) behavior
  }

  createNewAppt() {
    let newCreatetemplateEvent: any = {
      title: "New",
      start: new Date("2025-08-19T03:15:00.000Z"),
      end: new Date("2025-08-19T04:15:00.000Z"),
      color: "red",
      id: 1000111
    };
    this.events.push(newCreatetemplateEvent);
    this.hourSegmentClicked.emit({ date: new Date("2025-08-19T03:15:00.000Z")});
    setTimeout(() => {
      this.initCustomeAppointments();
    }, 500);
  }

combineDateTimeWithOneHour(date: number, month: number, year: number, timeStr: string): string {
  const [hours, minutes] = timeStr.split(":").map(Number);

  // Create a date in local time (IST)
  const dateObj = new Date(year, month, date, hours, minutes, 0);

  // Add 1 hour
  dateObj.setHours(dateObj.getHours() + 1);

  // Convert to ISO string (automatically in UTC)
  return dateObj.toISOString();
}


  formatToISO(day: string | Date, time: string) {
      // day: Date object or string like "Tue Aug 19 2025 00:00:00 GMT+0530"
      // time: "HH:mm", e.g., "16:00"

      const d = new Date(day);

      // Extract date components
      const year = d.getFullYear();
      const month = d.getMonth(); // 0-based, 0=Jan
      const date = d.getDate();

      // Extract hours and minutes from time string
      const [hours, minutes] = time.split(":").map(Number);

      // Create a new Date with local timezone
      const combined = new Date(year, month, date, hours, minutes);

      // Convert to ISO string in UTC
      return combined.toISOString();
    }

    addOneHour(time: string): string {
          let [hours, minutes] = time.split(":").map(Number);

          hours = (hours + 1) % 24; // add 1 hour, wrap around 24

          // Pad with leading zero if needed
          const newHours = hours.toString().padStart(2, "0");
          const newMinutes = minutes.toString().padStart(2, "0");

          return `${newHours}:${newMinutes}`;
  }

   backOneHour(time: string): string {
          let [hours, minutes] = time.split(":").map(Number);

          hours = (hours - 1) % 24; // add 1 hour, wrap around 24

          // Pad with leading zero if needed
          const newHours = hours.toString().padStart(2, "0");
          const newMinutes = minutes.toString().padStart(2, "0");

          return `${newHours}:${newMinutes}`;
  }

  clickTimeSlot(time, day) {
    const tt = time;
    const dd = day;
    const date = new Date(day).getDate() + 1;
    const month = new Date(day).getMonth();
    const year = new Date(day).getFullYear();
    const backOneHour = this.backOneHour(time);
    const datadate = this.combineDateTimeWithOneHour((date - 1), month, year, backOneHour);

    const addOneHrs = this.addOneHour(time);
    console.log("consoleData create 3dayview new app ", datadate);

     let newCreatetemplateEvent: any = {
      title: "New",
      start: new Date(this.formatToISO(new Date(day), time)),
      end: new Date(this.formatToISO(new Date(day), addOneHrs)),
      color: "#000000",
      id: 1000111
    };
    this.isNewInMobile = newCreatetemplateEvent;
    // this.allEventFromView.push(newCreatetemplateEvent);
    setTimeout(() => {
      this.initCustomeAppointments();
      this.cdr.markForCheck();
    }, 500);

   this.hourSegmentClicked.emit({ date: new Date(datadate)});
  }

  scrollToTimeMarker() {
     if (this.calendarRepository.daysInWeek === 3 && this.isMobileScreen) {
        const el = document.querySelector("#custom3daytime");
        if (el) {
          el?.scrollIntoView({ behavior: "auto", block: "start" });
        }
      }
  }
  checkSameApptForDiffCalendar() {
    // return;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.groupedAppointmentsData = [];
      console.log("checkSameApptForDiffCalendar");
      this.day3VisiblityAftersetPosition = true;
      const groupedAppointments = this.findDuplicateAppointments(this.appointments);
      if (groupedAppointments && groupedAppointments.length) {
        const lowestLesfAppointments = this.getLowestLesfFromGroups(groupedAppointments);
        console.log("lowestLesfAppointments", lowestLesfAppointments);
        groupedAppointments.forEach((group: any[], index: number) => {
          let anotherMain: boolean = false;

          group.map(ggrpp => {
            ggrpp["first_index"] = group[0].index;
            ggrpp["last_index"] = group[group.length - 1].index;
          });
          const lowest = lowestLesfAppointments[index];
          let main = group.find((appt: any) => typeof appt.folderId === "string" && !appt.folderId.includes(":"));
          if (!main) {
             main = group[0];
             anotherMain = true;
          }
          if (main) {
            let count = 1;
              group.map((grp, indx) => {
                if (typeof grp.folderId === "string" && grp.folderId.includes(":")) {
                  if (anotherMain) {
                    const top = grp.top;
                    const height = grp.height;
                    count = count + 1;
                    grp["groupIndex"] = count;
                    grp["moreAppointment"] = group.length - 4;
                    if (indx === 0) {
                      console.log("moreAppointment", grp.title, grp.width );
                    } else if (height > 38) {
                      grp.top = grp.top + (grp.height - 40);
                      grp.height = 40;
                    }

                    if (indx === group.length - 1) {
                        const mnLeft = main.left;
                        let singleWidth = this.customCellWidth / group.length;
                        let newleft = singleWidth * (group.length - 1);
                        grp.left = newleft + mnLeft - 11;
                        grp.width = singleWidth + 7;
                    }

                  } else {
                    const top = grp.top;
                    const height = grp.height;
                    count = count + 1;
                    grp["groupIndex"] = count;
                    grp["moreAppointment"] = group.length - 4;
                    if (height > 38) {
                      grp.top = grp.top + (grp.height - 40);
                      grp.height = 40;
                    }
                  }

                }
              });


              if (
                lowest &&
                typeof lowest.folderId === "string" &&
                lowest.folderId.includes(":") &&
                main
              ) {
                // Swap 'left' values
                const tempindex = lowest.index;
                const mainindex = main.index;
                const tempLeft = main.left;
                const lowestLeft = lowest.left;
                const lowestWidth = lowest.width;

                main.left = lowestLeft;
                lowest.left = tempLeft;

                // main.width = (lowestWidth * groupedAppointments[index].length) + (groupedAppointments[index].length * 2);
                main.width = this.customCellWidth - 4;

                main.index = tempindex;
                lowest.index = mainindex;
                main["groupIndex"] = 1;
              } else {
                console.log("mainIndex main data", main, main.width, lowest, groupedAppointments[index].length);
                main.width = main.width * groupedAppointments[index].length + 2;
              }
          }

        });


        groupedAppointments.map((grp, indx) => {
          grp.map((pg, index) => {
             const main = grp.find((appt: any) => typeof appt.folderId === "string" && !appt.folderId.includes(":"));

              if (main) {
                if (typeof pg.folderId === "string" && pg.folderId.includes(":")) {
                    if (grp.length - 1 !== index) {
                        pg.left = pg.left - (pg.width / 2);
                        pg.width = (pg.width * (groupedAppointments[indx].length - 2)) - 4;

                    } else if (grp.length - 1 === index) {
                      pg.left = pg.left - (pg.width / 2);
                      pg.width = pg.width + (pg.width / 2);
                      pg.width = pg.width + (groupedAppointments[indx].length * 2);
                    }

                    if (grp.length > 4) {
                       pg.width = pg.width + 7;
                    } else {
                      if (index === (grp.length - 1)) {
                          const mainLeft = main.left;
                          const curleft = pg.left;
                          const data = (pg.left - mainLeft);
                          pg.width = this.customCellWidth - data - 5;

                      } else {
                        pg.width = (this.customCellWidth / grp.length) + 7;

                      }
                      // pg.width = pg.width + 7;
                    }
                    // if (index === 3) {
                    //   pg.width = pg.width + 2;
                    // }
                }
              }

          });
        });
        this.cdr.markForCheck();
        console.log("checkSameApptForDiffCalendar", this.appointments, groupedAppointments, lowestLesfAppointments);
        this.groupedAppointmentsData = groupedAppointments;
      }
    }, 200);


  }


   findDuplicateAppointments(appointments: any[]): any[][] {
      const grouped: Record<string, any[]> = appointments.reduce((acc, appt) => {
        const inst = appt.inst?.[0];
        const title = appt.title?.trim().toLowerCase();

        const x_uid = appt.x_uid;
        if (!inst || !title) return acc;

        const key = `${inst.ridZ}-${title}`;

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(appt);
        return acc;
      }, {}); // inferred as Record<string, any[]>

      const duplicates: any[][] = [];

      for (const group of Object.values(grouped) as any[][]) {
        if (group.length > 1) {
          duplicates.push(group);
        }
      }

      return duplicates;
    }

     getLowestLesfFromGroups(groupedAppointments: any[][]): any[] {
      const lowestLesfInGroups: any[] = [];

      for (const group of groupedAppointments) {
        if (!group?.length) continue;

        const minLesf = Math.min(...group.map(appt => appt.left));
        const lowest = group.filter(appt => appt.left === minLesf);

        lowestLesfInGroups.push(...lowest);
      }

      return lowestLesfInGroups;
    }


    isInside(firstStart: Date, firstEnd: Date, secondStart: Date): boolean {
      console.log("openListForMoreAppt time", firstStart, firstEnd, secondStart );
      return secondStart.getTime() >= firstStart.getTime() &&
            secondStart.getTime() <= firstEnd.getTime();
    }

    openReplaceToFront(appt, dayIndex) {
      appt["selected"] = true;
      setTimeout(() => {
        appt["selected"] = false;
        this.cdr.markForCheck();
      }, 750);

      this.debounceTapHandlerSubject.next({appt, dayIndex});

      let fourthAppointmentData: any;
      let matchGroupIndex: any;
      this.groupedAppointmentsData.map((group, index) => {
        group.map(ap => {
          if (appt.inst[0].ridZ === ap.inst[0].ridZ &&  appt.inst[0].ridZ === ap.inst[0].ridZ && appt.title === ap.title) {
            matchGroupIndex = index;
          }
        });
      });

      if ( matchGroupIndex !== undefined) {
        this.groupedAppointmentsData[matchGroupIndex].map(ap => {
          if (ap?.groupIndex === 1) {
            fourthAppointmentData = ap;
          }
          ap.last_index = ap.last_index + 500;
          ap.index = ap.index + 500;
          setTimeout(() => {
            ap.index = ap.index - 500;
            ap.last_index = ap.last_index - 500;
            this.cdr.markForCheck();
          }, 2500);
        });


        const mainLeft = appt.left;
        const mainWidth = appt.width;
        const mainGroupindex = appt?.groupIndex;
        const mainindex = appt.index;
        const mainHeight = appt.height;
        const mainTop = appt.top;


        const replaceWithLeft = fourthAppointmentData.left;
        const replaceWithLWidth = fourthAppointmentData.width;
        const replaceWithLGroupindex = fourthAppointmentData?.groupIndex;
        const replaceWithLindex = fourthAppointmentData.index;
        const replaceWithLHeight = fourthAppointmentData.height;
        const replaceWithLTop = fourthAppointmentData.top;


        appt.left = replaceWithLeft;
        appt.width = replaceWithLWidth;
        appt.groupIndex = replaceWithLGroupindex;
        appt.index = replaceWithLindex;
        appt.height = replaceWithLHeight;
        appt.top = replaceWithLTop;

        fourthAppointmentData.left = mainLeft;
        fourthAppointmentData.width = mainWidth;
        fourthAppointmentData.groupIndex = mainGroupindex;
        fourthAppointmentData.index = mainindex;
        fourthAppointmentData.height = mainHeight;
        fourthAppointmentData.top = mainTop;
      }


    }


    openListForMoreAppt(index, appt) {
      console.log("openListForMoreAppt", index, this.daysCustom[index], appt);

      let Newdate;
      const base = new Date(this.selectedYear, this.selectedMonth, this.selectedDate); // July 25
      Newdate = addMinutes(base, (index) * 1440);

      let endDate = addMinutes(Newdate, 2 * 1400);
      setTimeout(() => {
        this.broadcaster.broadcast("DATE_RANGE_CHANGE", {
            start: base,
            end: Newdate
        });
        this.calendarRepository.dateChangeFor3dayviewToListView$.next({view: "list", startDate: Newdate});
        this.calendarRepository.dateChangeFor3dayviewToListViewForTime$.next({appt: appt});
      }, 450);
      this.handleListClicked.emit();
    }


  pastCheck(appt): boolean {
    return new Date() > appt.end;
  }

  private attachSwipeWhenReady(selector: string) {
    const tryAttach = () => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) return false;

      // Avoid double-binding if view re-renders
      if (this.hammerMap.has(el)) return true;

      // One Hammer instance, both directions
      const mc = new Hammer.Manager(el);
      mc.add(new Hammer.Swipe({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 10, velocity: 0.3 }));
      mc.on("swipeleft", () => this.onViewSwiped.emit("swipeleft"));
      mc.on("swiperight", () => this.onViewSwiped.emit("swiperight"));
      this.hammerMap.set(el, mc);

      // Optional: clean up when element is removed
      const ro = new ResizeObserver(() => { /* keep ref alive */ });
      ro.observe(el);
      return true;
    };

    // Try now; if not present, retry a few frames then fall back to a MutationObserver
    if (tryAttach()) return;

    let attempts = 0;
    const rafLoop = () => {
      attempts++;
      if (tryAttach() || attempts > 10) return;
      requestAnimationFrame(rafLoop);
    };
    requestAnimationFrame(rafLoop);

    // As a final fallback (e.g., view switches later), observe DOM mutations
    const mo = new MutationObserver(() => { if (tryAttach()) mo.disconnect(); });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  resizeApt(appt, ev) {
    console.log("resizeApt appt, ev", appt, ev);
  }

  resizeAptResizing(appt, ev) {
    console.log("resizeApting appt, ev", appt, ev);
  }

  resizeAptEnd(appt, ev) {
    console.log("resizeAptEnd appt, ev", appt, ev);
  }

  onResizeEnd(event: ResizeEvent): void {
    console.log("Element was resized", event);
  }

}
